import React from "react";
import {
  getAliasField,
  getCardBrand,
  getExpiryParts,
  validateCVV,
  validateCardNumberField,
  validateExpirationDate,
  validateNameOnCard,
} from "@/features/checkout/utils/validation";
import type { AddCardPayload } from "@/api/models/api-types";

/**
 * Kart formunun tek state + doğrulama kaynağı.
 *
 * Kart isteyen iki ekran var: hesaptaki "Kayıtlı Kartlarım" (kartı saklar,
 * `POST /cards`) ve checkout (kartla öder, `ClientCheckoutDto.card`). İkisi
 * aynı alanları toplar; DTO'lar arasındaki tek fark ödemenin CVC istemesidir.
 * O yüzden fark da tek bir `mode` ile ifade edilir:
 *
 *   `save`  numara · son kullanma · isim · alias (isteğe bağlı)
 *   `pay`   numara · son kullanma · CVV · isim · [kartı kaydet → alias]
 *
 * Alias her iki modda da isteğe bağlıdır — DTO'da öyle (`alias?: string`).
 * Checkout'un onu zorunlu tutması iki formu aynı alan için farklı davranır
 * hale getiriyordu.
 */

export type CardFormMode = "save" | "pay";

export type CardFormField = "cardNumber" | "expirationDate" | "cvv" | "nameOnCard";

export interface CardFormValues {
  cardNumber: string;
  expirationDate: string;
  cvv: string;
  nameOnCard: string;
  saveCard: boolean;
  cardAlias: string;
}

const EMPTY: CardFormValues = {
  cardNumber: "",
  expirationDate: "",
  cvv: "",
  nameOnCard: "",
  saveCard: false,
  cardAlias: "",
};

/** Kural hata kodları — `checkout.validation.<alan>.<KOD>` ile çevrilir. */
export type CardFormErrors = Partial<Record<CardFormField, string>>;

const fieldsFor = (mode: CardFormMode): CardFormField[] =>
  mode === "pay"
    ? ["cardNumber", "expirationDate", "cvv", "nameOnCard"]
    : ["cardNumber", "expirationDate", "nameOnCard"];

export const validateCardForm = (values: CardFormValues, mode: CardFormMode): CardFormErrors => {
  const errors: CardFormErrors = {};

  const cardNumber = validateCardNumberField(values.cardNumber);
  if (cardNumber) errors.cardNumber = cardNumber;

  const expiry = validateExpirationDate(values.expirationDate);
  if (!expiry.isValid) errors.expirationDate = expiry.error;

  if (mode === "pay") {
    const cvv = validateCVV(values.cvv, getCardBrand(values.cardNumber));
    if (!cvv.isValid) errors.cvv = cvv.error;
  }

  const name = validateNameOnCard(values.nameOnCard);
  if (!name.isValid) errors.nameOnCard = name.error;

  return errors;
};

export const useCardForm = (mode: CardFormMode) => {
  const [values, setValues] = React.useState<CardFormValues>(EMPTY);
  // Hatalar blur'da görünür, her tuşta değil — dört rakamdan sonra "kart
  // numarası geçersiz" demek gürültüdür.
  const [touched, setTouched] = React.useState<Partial<Record<CardFormField, true>>>({});

  const errors = React.useMemo(() => validateCardForm(values, mode), [values, mode]);
  const isValid = Object.keys(errors).length === 0;

  // Yalnızca kullanıcının terk ettiği alanların hataları
  const visibleErrors = React.useMemo(() => {
    const shown: CardFormErrors = {};
    for (const field of fieldsFor(mode)) {
      if (touched[field] && errors[field]) shown[field] = errors[field];
    }
    return shown;
  }, [errors, touched, mode]);

  const set = React.useCallback((patch: Partial<CardFormValues>) => {
    setValues((current) => ({ ...current, ...patch }));
  }, []);

  const touch = React.useCallback((field: CardFormField) => {
    setTouched((current) => ({ ...current, [field]: true }));
  }, []);

  /** Gönderimde: her alanı dokunulmuş say ki hatalar bir anda görünsün. */
  const touchAll = React.useCallback(() => {
    setTouched(Object.fromEntries(fieldsFor(mode).map((f) => [f, true])));
  }, [mode]);

  const reset = React.useCallback(() => {
    setValues(EMPTY);
    setTouched({});
  }, []);

  const detectedBrand = React.useMemo(() => getCardBrand(values.cardNumber), [values.cardNumber]);

  /** `POST /cards` gövdesi — `SaveCardDto`. CVC yok. */
  const toSaveCardPayload = React.useCallback(
    (): AddCardPayload => ({
      cardHolderName: values.nameOnCard.trim(),
      cardNumber: values.cardNumber.replace(/\D/g, ""),
      ...getExpiryParts(values.expirationDate),
      ...getAliasField(values.cardAlias),
    }),
    [values]
  );

  /** `ClientCheckoutDto.card` gövdesi — `BankCardDto`. */
  const toPaymentCardPayload = React.useCallback(
    () => ({
      cardHolderName: values.nameOnCard.trim(),
      cardNumber: values.cardNumber.replace(/\D/g, ""),
      ...getExpiryParts(values.expirationDate),
      cvc: values.cvv,
      ...(values.saveCard ? { saveCard: true, ...getAliasField(values.cardAlias) } : {}),
    }),
    [values]
  );

  return {
    mode,
    values,
    errors: visibleErrors,
    isValid,
    detectedBrand,
    set,
    touch,
    touchAll,
    reset,
    toSaveCardPayload,
    toPaymentCardPayload,
  };
};

export type CardForm = ReturnType<typeof useCardForm>;
